import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { useTranslation, withTranslation } from 'react-i18next';
import isEmpty from 'lodash/isEmpty';
import pick from 'lodash/pick';
import MaterialIcon from 'material-icons-react';
import { Input, ZipForm, ModalInfo } from 'shared/components';
import { validationRules, errorStrings } from 'shared/validations';
import { LocationModel } from 'shared/models';
import { ValidationService } from 'shared/services';

import './EditReportingLocation.scss';

const INPUT_MAX_LENGTH = 36;

const rules = pick(validationRules, [
  'name',
  'address1',
  'countryCode',
  'postalCode',
  'city',
  'stateCode',
]);
rules.area = ['required'];
const errStr = pick(errorStrings, [
  'name',
  'address1',
  'countryCode',
  'postalCode',
  'city',
  'stateCode',
]);
errStr.name = { required: 'errors.locationNameRequired' };
errStr.area = { required: 'errors.locationDescriptionRequired' };

class EditReportingLocation extends Component {
  constructor(props) {
    super(props);

    this.state = {
      location: this.props.addNew ? new LocationModel({}) : this.props.location,
      modalOpen: false,
      errors: {},
    };

    this.toggleModal = this.toggleModal.bind(this);
    this.saveLocation = this.saveLocation.bind(this);
    this.handleZipChange = this.handleZipChange.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.onCheckChange = this.onCheckChange.bind(this);
    this.checkSameAddress = this.checkSameAddress.bind(this);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.location !== this.props.location) {
      this.setState({
        location: this.props.location,
      });
    }
  }

  toggleModal() {
    const { location, institutionAddress } = this.props;
    location.sameAddress = this.checkSameAddress(location, institutionAddress);

    this.setState((prevState) => ({
      modalOpen: !prevState.modalOpen,
      location,
      errors: {},
    }));
  }

  checkSameAddress(location, institutionAddress) {
    let sameAddress = true;
    const commonFields = ['address1', 'postalCode', 'city', 'stateCode'];
    commonFields.forEach((field) =>
      location[field] != institutionAddress[field] ? (sameAddress = false) : ''
    );
    return sameAddress;
  }

  handleZipChange(address) {
    this.setState((prevState) => ({
      location: {
        ...prevState.location,
        ...address,
      },
      errors: {},
    }));
  }

  handleChange(e) {
    const { target } = e;
    if (target.value.length > INPUT_MAX_LENGTH) {
      this.setState({
        errors: {
          [target.name]: this.props.t('errors.fieldLimitation', { limit: INPUT_MAX_LENGTH }),
        },
      });
      return;
    }

    if (target.value && !ValidationService.specialCharsPermissive(target.value)) {
      this.setState({
        errors: {
          [target.name]: this.props.t('errors.specialCharactersNotAllowed2'),
        },
      });
      return;
    }

    this.setState((prevState) => ({
      location: {
        ...prevState.location,
        [target.name]: target.value,
      },
      errors: {},
    }));
  }

  onCheckChange(e) {
    const { checked, name } = e.target;
    const { location } = this.state;
    const { institutionAddress } = this.props;
    const commonFields = ['address1', 'postalCode', 'city', 'stateCode'];
    const updatedLocation = {};

    updatedLocation[name] = checked;

    if (checked) {
      commonFields.forEach((field) => (updatedLocation[field] = institutionAddress[field]));
    } else {
      commonFields.forEach((field) => (updatedLocation[field] = ''));
    }

    this.setState({
      location: {
        ...location,
        ...updatedLocation,
      },
    });
  }

  saveLocation() {
    const errors = ValidationService.validate(this.state.location, rules, errStr);

    if (!isEmpty(errors)) {
      this.setState({
        errors,
      });
      return;
    }

    this.props.onSave(this.state.location);
    this.setState({
      modalOpen: false,
    });
  }

  render() {
    const { location, modalOpen, errors } = this.state;
    const { addNew, institutionAddress } = this.props;
    const modalHeader = addNew
      ? 'testCenter.reportingLocations.addLocationHeaderLabel'
      : 'testCenter.reportingLocations.editLocationHeaderLabel';
    const buttonLabel = addNew
      ? 'testCenter.reportingLocations.addLocationHeaderLabel'
      : 'testCenter.reportingLocations.saveReportingLocation';
    const editLabel = this.props.editLabel || (
      <span>
        <MaterialIcon icon="edit" size="tiny" />
        {this.props.t('testCenter.reportingLocations.editLocationLabel')}
      </span>
    );
    return (
      <section className="edit-reporting-location">
        <ToggleButton addNew={addNew} toggle={this.toggleModal} editLabel={editLabel} />
        <ModalInfo
          className="modal-reporting-location"
          modalIsOpen={modalOpen}
          closeModal={this.toggleModal}
          onSubmit={this.saveLocation}
          headerTitle={modalHeader}
          saveLabel={buttonLabel}>
          <p className="location-information">
            {this.props.t('testCenter.reportingLocations.locationInformationLabel')}
          </p>
          <div className="edit-location-modal">
            <div className="list-group-item">
              <h6>
                {this.props.t('testCenter.reportingLocations.institution')}:{' '}
                {institutionAddress.name}
              </h6>

              <Input
                size={errors.name ? 'w-100' : 'w-100 input-details'}
                type="text"
                name="name"
                value={location.name}
                onChange={this.handleChange}
                errors={errors.name}
                label="testCenter.reportingLocations.locationNameLabel"
              />
              {!errors.name && (
                <div className="input-info">
                  {this.props.t('testCenter.reportingLocations.locationNameExample')}
                </div>
              )}
              <Input
                size={errors.area ? 'w-100' : 'w-100 input-details'}
                type="text"
                name="area"
                value={location.area}
                onChange={this.handleChange}
                errors={errors.area}
                label="testCenter.reportingLocations.locationAreaLabel"
              />
              {!errors.area && (
                <div className="input-info">
                  {this.props.t('testCenter.reportingLocations.locationAreaExample')}
                </div>
              )}
              <input
                type="checkbox"
                name="sameAddress"
                id="sameAddress"
                checked={location.sameAddress}
                onChange={this.onCheckChange}
              />
              <label className="form-check-label" htmlFor="sameAddress">
                {this.props.t('testCenter.reportingLocations.sameLocationAsInstitution')}
              </label>

              <Input
                size="w-100"
                type="text"
                name="address1"
                value={location.address1}
                onChange={this.handleChange}
                errors={errors.address1}
                label="common.labels.address"
                readOnly={location.sameAddress}
              />
            </div>

            <ZipForm
              address={location}
              handleChange={this.handleZipChange}
              errors={errors}
              readOnly={location.sameAddress}
            />
          </div>
        </ModalInfo>
      </section>
    );
  }
}

const ToggleButton = ({ addNew = false, toggle, editLabel }) => {
  const { t } = useTranslation();
  return addNew ? (
    <button className="btn btn-primary btn-small w-100" onClick={toggle}>
      {t('testCenter.reportingLocations.addLocationLabel')}
    </button>
  ) : (
    <button className="btn-link text-pacific-blue text-strong" onClick={toggle}>
      {editLabel}
    </button>
  );
};

EditReportingLocation.propTypes = {
  addNew: PropTypes.bool,
  location: PropTypes.shape({}),
  onSave: PropTypes.func.isRequired,
  editLabel: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
  t: PropTypes.func.isRequired,
};

EditReportingLocation.defaultProps = {
  addNew: false,
  location: {},
  editLabel: undefined,
};

ToggleButton.propTypes = {
  addNew: PropTypes.bool,
  toggle: PropTypes.func.isRequired,
  editLabel: PropTypes.node.isRequired,
  t: PropTypes.func.isRequired,
};

ToggleButton.defaultProps = {
  addNew: false,
};

export default withTranslation()(EditReportingLocation);
